import React from 'react';
import { useSelector } from 'react-redux';

import {
    useTranslator,
    useCurrentLanguage,
    useUrlLocalizer
} from 'tradera-lang/translate';

import { isSwedishOrUndefined } from 'tradera-utils/languagecode';
import ConsentService from 'tradera-utils/consent-service';
import { useIsFeatureEnabled } from 'tradera-hooks/use-is-feature-enabled';
import styles from './footer-links.module.scss';

const FooterLinks = ({ links, callerId }) => {
    const { language } = useCurrentLanguage();

    const linksToRender =
        language !== 'sv' ? links.filter(link => !link.onlySwedish) : links;

    return (
        <>
            {linksToRender.map(
                ({ text, className, onlySwedish, ...rest }, index) => {
                    return (
                        <li
                            key={`${callerId}-${index}`}
                            className={`pb-1 pr-2 ${
                                className !== undefined ? className : ''
                            }`}>
                            <a {...rest}>{text}</a>
                        </li>
                    );
                }
            )}
        </>
    );
};

export const BuyingLinks = () => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const isNextWebBrandsPageEnabled = useSelector(
        state => !!state.environment.featureSwitches['next-web-brands-page']
    );

    const links = [
        {
            href: toLocalizedUrl('/how-to-buy'),
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Så handlar du på Tradera',
            text: t('footer_howToShop'),
            onlySwedish: false
        },
        {
            href: toLocalizedUrl('/kategorier'),
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Kategorier',
            text: t('footer_categories'),
            onlySwedish: false
        },
        {
            href: toLocalizedUrl(
                `/brands${isNextWebBrandsPageEnabled ? '?next=1' : ''}`
            ),
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Populära varumärken',
            text: t('footer_popularBrands'),
            onlySwedish: false
        },
        {
            href: toLocalizedUrl('/inspiration'),
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Inspiration',
            text: t('footer_inspiration'),
            onlySwedish: true
        },
        {
            href: '/info/gronfrakt/',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Grön frakt på Tradera',
            text: t('footer_greenShipping'),
            onlySwedish: true
        }
    ];

    return (
        <div className={`${styles.linkContainer} col-6 col-md-3 mx-md-0 mb-3`}>
            <h4 className="text-styled">{t('footer_buyHeading')}</h4>
            <ul className="plain-list">
                <FooterLinks callerId="buyinglinks" links={links} />
            </ul>
        </div>
    );
};

export const SellingLinks = () => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const isPennybridgeCharitiesEnabled = useIsFeatureEnabled(
        'pennybridge-charities'
    );

    const links = [
        {
            href: toLocalizedUrl('/how-to-sell'),
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Så säljer du som privatperson',
            text: t('footer_howToSellPrivate'),
            onlySwedish: false
        },
        {
            href: 'https://info.tradera.com/foretag/',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Sälj som företag',
            text: t('footer_howToSellCorporate'),
            onlySwedish: true
        },
        isPennybridgeCharitiesEnabled && {
            href: toLocalizedUrl('/causes'),
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Donera med Tradera',
            text: t('footer_donateWithTradera'),
            onlySwedish: true
        },
        {
            href: '/frakt',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Fraktkalkylator',
            text: t('footer_shippingCalculator'),
            onlySwedish: true
        },
        {
            href: 'https://info.tradera.com/butik-privat/',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Butik',
            text: t('footer_store'),
            onlySwedish: true
        }
    ].filter(Boolean);

    return (
        <div className={`${styles.linkContainer} col-6 col-md-3 mx-md-0 mb-3`}>
            <h4 className="text-styled">{t('footer_sellHeading')}</h4>
            <ul className="plain-list">
                <FooterLinks callerId="sellinglinks" links={links} />
            </ul>
        </div>
    );
};

export const ContactAndHelpLinks = ({ pageName, openGdprSettings }) => {
    const { t } = useTranslator();
    const { language } = useCurrentLanguage();
    const isQuantcastConsentEnabled = useIsFeatureEnabled('quantcast-consent');

    const links = [
        {
            href: 'https://info.tradera.com/sakerhetscenter/regler-pa-tradera/',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Regler på Tradera',
            text: t('footer_traderaRules'),
            onlySwedish: true
        },
        {
            href: 'http://www.mynewsdesk.com/se/tradera',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Press',
            text: t('footer_press'),
            rel: 'nofollow',
            onlySwedish: true
        },
        {
            href: 'https://info.tradera.com/sakerhetscenter',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Säkerhet',
            text: t('footer_security'),
            onlySwedish: true
        },
        {
            href: isSwedishOrUndefined(language)
                ? 'https://info.tradera.com/'
                : 'https://info.tradera.com/en/',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Vanliga frågor',
            text: t('footer_faq'),
            onlySwedish: false
        },
        {
            href: 'https://www.tradera.com/info/blogg/',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Vad är nytt',
            text: t('footer_whatsUp'),
            onlySwedish: true
        },
        {
            href:
                'http://info.tradera.com/#humany-vanliga-fragor-och-svar=/contact',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Kontakta oss',
            text: t('footer_contactUs'),
            onlySwedish: true
        },
        {
            href:
                pageName === 'SellItem'
                    ? '//tradera.humany.net/syi'
                    : '//tradera.humany.net/juno',
            text: t('footer_customerService'),
            rel: 'nofollow',
            className: 'd-none',
            onlySwedish: false
        },
        {
            href: '#',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Open GDPR Settings',
            text: t('footer_gdpr_settings'),
            onClick: event => {
                event.preventDefault();
                if (isQuantcastConsentEnabled) {
                    ConsentService.openUi();
                } else {
                    openGdprSettings();
                }
            },
            onlySwedish: false
        }
    ];

    return (
        <div className={`${styles.linkContainer} col-6 col-md-3 mx-md-0 mb-3`}>
            <h4 className="text-styled">{t('footer_contactHelpHeading')}</h4>
            <ul className="plain-list">
                <FooterLinks callerId="contactandhelplinks" links={links} />
            </ul>
        </div>
    );
};

export const CouponCodeLinks = ({ isCouponationNavLinkEnabled }) => {
    const { t } = useTranslator();
    const { language } = useCurrentLanguage();
    const links = [
        {
            href: '/rabattkoder/cdon',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'CDON',
            text: 'CDON',
            onlySwedish: true
        },
        {
            href: '/rabattkoder/ellos',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Ellos',
            text: 'Ellos',
            onlySwedish: true
        },
        {
            href: '/rabattkoder/hotels-com',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Hotels.com',
            text: 'Hotels.com',
            onlySwedish: true
        },
        {
            href: '/rabattkoder/jotex',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Jotex',
            text: 'Jotex',
            onlySwedish: true
        },
        {
            href: '/rabattkoder/nelly',
            'data-ga-category': 'Footer',
            'data-ga-action': 'Navigation',
            'data-ga-label': 'Nelly',
            text: 'Nelly',
            onlySwedish: true
        }
    ];

    return isSwedishOrUndefined(language) ? (
        <div className={`${styles.linkContainer} col-6 col-md-3 mx-md-0 mb-3`}>
            {isCouponationNavLinkEnabled && (
                <>
                    <h4 className="text-styled">{t('footer_couponCodes')}</h4>
                    <ul className="plain-list">
                        <FooterLinks callerId="couponcodelinks" links={links} />
                    </ul>
                </>
            )}
        </div>
    ) : null;
};
